import { useEffect, useState } from "react";
import {
  BarChart3,
  CalendarDays,
  Check,
  Crown,
  Flame,
  Gauge,
  Infinity as InfinityIcon,
  Layers,
  Loader2,
  MessageCircle,
  Minus,
  Package,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Zap,
} from "lucide-react";
import { api, ApiError } from "../lib/api";
import { AccountOut } from "../types";
import { openExternalLink } from "../lib/telegram";

const CONTACT_LINK = "https://t.me/hhs9h";

interface DayCount {
  date: string;
  count: number;
}
interface StatsOut {
  tier: string;
  expires_at: number | null;
  total: number;
  today: number;
  week: number;
  prev_week: number;
  month: number;
  daily: DayCount[];
  best_day: DayCount | null;
  active_days_week: number;
  avg_per_active_day: number;
  packs_count: number;
  daily_limit: number | null;
}

const MONTHS = ["كانون الثاني", "شباط", "آذار", "نيسان", "أيار", "حزيران", "تموز", "آب", "أيلول", "تشرين الأول", "تشرين الثاني", "كانون الأول"];
const WEEKDAYS = ["أحد", "اثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"];

function fmtDate(ts: number): string {
  const d = new Date(ts * 1000);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
function fmtDay(iso: string): string {
  const [, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]}`;
}
function weekday(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
}
const n = (v: number) => v.toLocaleString("en-US");

const PLANS = {
  pro: {
    key: "pro" as const,
    name: "PRO",
    days: 15,
    price: "5,000",
    tagline: "للي يصمم كثير وبدون قيود الاستخدام اليومي",
    icon: Zap,
    features: [
      "100 ملصق باليوم",
      "كل القوالب حتى قوالب VIP المميزة",
      "الإنشاء الدفعي: عدّة أسماء بضغطة وحدة",
      "كل أدوات التحرير والحركات والتأثيرات الحركية",
      "رسم الاسم بالقلم بكل خياراته",
      "التراجع والإعادة أثناء التصميم",
    ],
  },
  vip: {
    key: "vip" as const,
    name: "VIP",
    days: 30,
    price: "10,000",
    tagline: "كل شي مفتوح بدون حدود",
    icon: Crown,
    features: [
      "ملصقات بلا حد يومي",
      "كل مميزات PRO",
      "الرسم بالذكاء الاصطناعي",
      "قلم النيون الحصري بالرسم الحر",
      "آخر المشاريع: نحفظ لك آخر 3 مشاريع لمدة 3 أيام",
    ],
  },
};

const MATRIX: { label: string; free: string | boolean; pro: string | boolean; vip: string | boolean }[] = [
  { label: "الملصقات باليوم", free: "2", pro: "100", vip: "بلا حد" },
  { label: "قوالب VIP المميزة", free: false, pro: true, vip: true },
  { label: "الإنشاء الدفعي", free: false, pro: true, vip: true },
  { label: "الحركات والتأثيرات ورسم الاسم", free: true, pro: true, vip: true },
  { label: "الرسم بالذكاء الاصطناعي", free: false, pro: false, vip: true },
  { label: "قلم النيون الحصري", free: false, pro: false, vip: true },
  { label: "آخر المشاريع", free: false, pro: false, vip: true },
];

function Cell({ v }: { v: string | boolean }) {
  if (v === true) return <Check size={15} strokeWidth={3} style={{ color: "#6EE7A8" }} />;
  if (v === false) return <Minus size={15} className="text-tg-hint opacity-50" />;
  return <span className="text-xs font-bold">{v}</span>;
}

function ContactButton({ label, strong = true }: { label: string; strong?: boolean }) {
  return (
    <button
      type="button"
      onClick={() => openExternalLink(CONTACT_LINK)}
      className="w-full h-12 rounded-2xl flex items-center justify-center gap-2 text-sm font-bold text-white active:scale-[.98] transition-transform"
      style={
        strong
          ? { background: "linear-gradient(135deg,#4C8DFF,#2F5BEA)", boxShadow: "0 12px 26px -12px rgba(59,130,246,.95), inset 0 1px 0 rgba(255,255,255,.25)" }
          : { background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.14)" }
      }
    >
      <MessageCircle size={18} strokeWidth={2.25} />
      {label}
    </button>
  );
}

function PlanCard({ plan, featured }: { plan: (typeof PLANS)["pro"] | (typeof PLANS)["vip"]; featured?: boolean }) {
  const Icon = plan.icon;
  return (
    <div
      className="relative rounded-3xl p-4 flex flex-col gap-3 overflow-hidden"
      style={{
        background: featured ? "linear-gradient(160deg, rgba(59,130,246,.22), rgba(20,30,60,.55))" : "rgba(255,255,255,.045)",
        border: featured ? "1px solid rgba(96,165,250,.55)" : "1px solid rgba(255,255,255,.1)",
        boxShadow: featured ? "0 18px 40px -22px rgba(59,130,246,.9)" : undefined,
      }}
    >
      {featured && (
        <span className="absolute top-3 left-3 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(59,130,246,.9)", color: "#fff" }}>
          الأفضل
        </span>
      )}
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: "rgba(59,130,246,.18)", border: "1px solid rgba(96,165,250,.35)" }}>
          <Icon size={22} strokeWidth={2.25} style={{ color: "#8FB4FF" }} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-lg font-extrabold tracking-wide">{plan.name}</div>
          <div className="text-[11px] text-tg-hint truncate">{plan.tagline}</div>
        </div>
      </div>

      <div className="flex items-end gap-2">
        <span className="text-3xl font-extrabold tabular-nums leading-none">{plan.price}</span>
        <span className="text-xs text-tg-hint pb-0.5">دينار عراقي</span>
        <span className="mr-auto text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: "rgba(255,255,255,.08)" }}>
          لمدة {plan.days} يوم
        </span>
      </div>

      <ul className="flex flex-col gap-2 pt-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-[13px] leading-snug">
            <span className="mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(110,231,168,.16)" }}>
              <Check size={11} strokeWidth={3.5} style={{ color: "#6EE7A8" }} />
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <ContactButton label={`اشترك بباقة ${plan.name}`} strong={featured} />
    </div>
  );
}

function PlansView() {
  return (
    <div className="flex flex-col gap-4">
      <div className="px-1 pt-1">
        <h1 className="text-2xl font-bold tracking-tight">الباقات</h1>
        <p className="text-sm text-tg-hint mt-1">اختر الباقة اللي تناسبك، والاشتراك عن طريق الدعم</p>
      </div>

      <PlanCard plan={PLANS.vip} featured />
      <PlanCard plan={PLANS.pro} />

      <div className="rounded-3xl overflow-hidden" style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.1)" }}>
        <div className="px-4 pt-3 pb-2 text-sm font-bold flex items-center gap-2">
          <Layers size={15} className="text-tg-hint" />
          مقارنة المميزات
        </div>
        <div className="grid text-center text-[11px] text-tg-hint px-3 pb-1.5" style={{ gridTemplateColumns: "1.7fr 1fr 1fr 1fr" }}>
          <span />
          <span>مجاني</span>
          <span style={{ color: "#8FB4FF" }}>PRO</span>
          <span style={{ color: "#8FB4FF" }}>VIP</span>
        </div>
        {MATRIX.map((r) => (
          <div key={r.label} className="grid items-center text-center px-3 py-2.5 border-t border-white/[0.06]" style={{ gridTemplateColumns: "1.7fr 1fr 1fr 1fr" }}>
            <span className="text-[12px] text-right leading-snug">{r.label}</span>
            <span className="flex justify-center"><Cell v={r.free} /></span>
            <span className="flex justify-center"><Cell v={r.pro} /></span>
            <span className="flex justify-center"><Cell v={r.vip} /></span>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2 pb-2">
        <ContactButton label="تواصل مع الدعم للاشتراك" />
        <p className="text-[11px] text-tg-hint text-center">الاشتراك يتفعّل خلال دقائق من التواصل مع الدعم</p>
      </div>
    </div>
  );
}

function Ring({ value, label, sub }: { value: number; label: string; sub: string }) {
  const r = 46;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative shrink-0" style={{ width: 112, height: 112 }}>
      <svg width="112" height="112" viewBox="0 0 112 112" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="56" cy="56" r={r} fill="none" stroke="rgba(255,255,255,.1)" strokeWidth="9" />
        <circle
          cx="56"
          cy="56"
          r={r}
          fill="none"
          stroke="url(#ringGrad)"
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - Math.max(0.02, Math.min(1, value)))}
        />
        <defs>
          <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#8FB4FF" />
            <stop offset="1" stopColor="#3B82F6" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-extrabold tabular-nums leading-none">{label}</span>
        <span className="text-[10px] text-tg-hint mt-1">{sub}</span>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, value, label, hint }: { icon: typeof Gauge; value: string; label: string; hint?: string }) {
  return (
    <div className="rounded-2xl p-3 flex flex-col gap-1" style={{ background: "rgba(255,255,255,.045)", border: "1px solid rgba(255,255,255,.1)" }}>
      <span className="flex items-center gap-1.5 text-[11px] text-tg-hint">
        <Icon size={13} strokeWidth={2.25} style={{ color: "#8FB4FF" }} />
        {label}
      </span>
      <span className="text-2xl font-extrabold tabular-nums leading-none">{value}</span>
      {hint && <span className="text-[10px] text-tg-hint">{hint}</span>}
    </div>
  );
}

function SubscriberView({ account, stats }: { account: AccountOut; stats: StatsOut | null }) {
  const tier = account.membership.tier === "vip" ? PLANS.vip : PLANS.pro;
  const Icon = tier.icon;
  const exp = account.membership.expires_at;
  const nowS = Date.now() / 1000;
  const left = exp ? Math.max(0, exp - nowS) : null;
  const daysLeft = left === null ? null : Math.floor(left / 86400);
  const hoursLeft = left === null ? null : Math.floor((left % 86400) / 3600);
  const total = Math.max(tier.days, daysLeft ?? 0);
  const ringValue = left === null ? 1 : left / 86400 / total;
  const ringLabel = daysLeft === null ? "∞" : daysLeft > 0 ? String(daysLeft) : String(hoursLeft ?? 0);
  const ringSub = daysLeft === null ? "دائمة" : daysLeft > 0 ? "يوم متبقي" : "ساعة متبقية";

  const daily = stats?.daily ?? [];
  const last7 = daily.slice(-7);
  const max7 = Math.max(1, ...last7.map((d) => d.count));
  const delta = stats && stats.prev_week > 0 ? Math.round(((stats.week - stats.prev_week) / stats.prev_week) * 100) : null;
  const limit = stats?.daily_limit ?? account.daily_limit;
  const todayUsed = stats?.today ?? account.daily_used;

  return (
    <div className="flex flex-col gap-4">
      <div className="px-1 pt-1">
        <h1 className="text-2xl font-bold tracking-tight">اشتراكي</h1>
        <p className="text-sm text-tg-hint mt-1">باقتك الحالية وإحصائيات استخدامك</p>
      </div>

      {/* بطاقة الباقة */}
      <div
        className="relative rounded-3xl p-4 overflow-hidden"
        style={{
          background: "linear-gradient(155deg, rgba(59,130,246,.30), rgba(20,30,60,.65) 70%)",
          border: "1px solid rgba(96,165,250,.5)",
          boxShadow: "0 20px 44px -24px rgba(59,130,246,.95)",
        }}
      >
        <div className="flex items-center gap-3.5">
          <Ring value={ringValue} label={ringLabel} sub={ringSub} />
          <div className="flex-1 min-w-0 flex flex-col gap-2">
            <span className="inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full text-[11px] font-bold" style={{ background: "rgba(110,231,168,.16)", color: "#6EE7A8" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#6EE7A8" }} />
              اشتراك فعّال
            </span>
            <div className="flex items-center gap-2">
              <Icon size={22} strokeWidth={2.25} style={{ color: "#8FB4FF" }} />
              <span className="text-2xl font-extrabold tracking-wide">{tier.name}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[12px] text-tg-hint">
              <CalendarDays size={13} />
              {exp ? `ينتهي ${fmtDate(exp)}` : "بدون تاريخ انتهاء"}
            </div>
            {daysLeft !== null && daysLeft > 0 && hoursLeft !== null && (
              <div className="text-[11px] text-tg-hint">باقي {daysLeft} يوم و{hoursLeft} ساعة</div>
            )}
          </div>
        </div>
      </div>

      {!stats && (
        <div className="flex justify-center py-6 text-tg-hint">
          <Loader2 className="animate-spin" size={20} />
        </div>
      )}

      {stats && (
        <>
          {/* الاستخدام اليومي */}
          <div className="rounded-3xl p-4 flex flex-col gap-2.5" style={{ background: "rgba(255,255,255,.045)", border: "1px solid rgba(255,255,255,.1)" }}>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm font-bold">
                <Gauge size={16} style={{ color: "#8FB4FF" }} />
                استخدام اليوم
              </span>
              <span className="text-sm font-extrabold tabular-nums flex items-center gap-1">
                {n(todayUsed)}
                <span className="text-tg-hint font-medium">/</span>
                {limit === null ? <InfinityIcon size={16} strokeWidth={2.5} /> : n(limit)}
              </span>
            </div>
            <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,.08)" }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: `${limit ? Math.min(100, (todayUsed / limit) * 100) : Math.min(100, todayUsed * 4)}%`,
                  background: "linear-gradient(90deg,#4C8DFF,#8FB4FF)",
                  transition: "width .6s ease",
                }}
              />
            </div>
            <span className="text-[11px] text-tg-hint">
              {limit === null ? "ملصقات بلا حد يومي" : `باقي ${n(Math.max(0, limit - todayUsed))} ملصق اليوم`} · يتجدد الحد الساعة 3 فجراً بتوقيت بغداد
            </span>
          </div>

          {/* الأرقام */}
          <div className="grid grid-cols-2 gap-2.5">
            <StatTile icon={Sparkles} value={n(stats.total)} label="إجمالي الملصقات" hint="من أول استخدام" />
            <StatTile icon={BarChart3} value={n(stats.week)} label="آخر 7 أيام" hint={`${stats.active_days_week} أيام نشاط`} />
            <StatTile icon={CalendarDays} value={n(stats.month)} label="آخر 30 يوم" />
            <StatTile icon={Package} value={n(stats.packs_count)} label="حزمك" />
          </div>

          {/* الرسم الأسبوعي */}
          <div className="rounded-3xl p-4 flex flex-col gap-3" style={{ background: "rgba(255,255,255,.045)", border: "1px solid rgba(255,255,255,.1)" }}>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm font-bold">
                <BarChart3 size={16} style={{ color: "#8FB4FF" }} />
                نشاطك الأسبوعي
              </span>
              {delta !== null && (
                <span
                  className="flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full"
                  style={{ background: delta >= 0 ? "rgba(110,231,168,.14)" : "rgba(248,113,113,.14)", color: delta >= 0 ? "#6EE7A8" : "#F87171" }}
                >
                  {delta >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  {Math.abs(delta)}% عن الأسبوع اللي قبله
                </span>
              )}
            </div>
            <div className="flex items-end justify-between gap-1.5" style={{ height: 120 }}>
              {last7.map((d, i) => {
                const isToday = i === last7.length - 1;
                const h = d.count === 0 ? 4 : Math.max(10, (d.count / max7) * 92);
                return (
                  <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1.5 h-full">
                    <span className="text-[10px] tabular-nums text-tg-hint">{d.count > 0 ? d.count : ""}</span>
                    <div
                      className="w-full rounded-lg"
                      style={{
                        height: h,
                        background: d.count === 0 ? "rgba(255,255,255,.08)" : isToday ? "linear-gradient(180deg,#8FB4FF,#3B82F6)" : "linear-gradient(180deg,rgba(143,180,255,.65),rgba(59,130,246,.55))",
                        transition: "height .6s ease",
                      }}
                    />
                    <span className={`text-[9px] ${isToday ? "font-bold" : "text-tg-hint"}`}>{weekday(d.date)}</span>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <div className="rounded-xl px-3 py-2 flex items-center gap-2" style={{ background: "rgba(255,255,255,.05)" }}>
                <Flame size={15} style={{ color: "#8FB4FF" }} />
                <div className="min-w-0">
                  <div className="text-[10px] text-tg-hint">أفضل يوم</div>
                  <div className="text-xs font-bold">{stats.best_day ? `${stats.best_day.count} ملصق` : "—"}</div>
                  {stats.best_day && <div className="text-[10px] text-tg-hint">{fmtDay(stats.best_day.date)}</div>}
                </div>
              </div>
              <div className="rounded-xl px-3 py-2 flex items-center gap-2" style={{ background: "rgba(255,255,255,.05)" }}>
                <TrendingUp size={15} style={{ color: "#8FB4FF" }} />
                <div className="min-w-0">
                  <div className="text-[10px] text-tg-hint">المعدل باليوم النشط</div>
                  <div className="text-xs font-bold">{stats.avg_per_active_day ? `${stats.avg_per_active_day} ملصق` : "—"}</div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* مميزات باقتك */}
      <div className="rounded-3xl p-4 flex flex-col gap-2.5" style={{ background: "rgba(255,255,255,.045)", border: "1px solid rgba(255,255,255,.1)" }}>
        <span className="text-sm font-bold flex items-center gap-2">
          <Crown size={15} style={{ color: "#8FB4FF" }} />
          مميزات باقتك
        </span>
        {tier.features.map((f) => (
          <div key={f} className="flex items-start gap-2 text-[13px]">
            <Check size={14} strokeWidth={3} className="mt-0.5 shrink-0" style={{ color: "#6EE7A8" }} />
            <span>{f}</span>
          </div>
        ))}
      </div>

      {/* تجديد / ترقية */}
      {account.membership.tier === "pro" ? (
        <div className="flex flex-col gap-3">
          <PlanCard plan={PLANS.vip} featured />
          <ContactButton label="تجديد باقة PRO" strong={false} />
        </div>
      ) : (
        <ContactButton label="تجديد الاشتراك" />
      )}
      <div className="pb-2" />
    </div>
  );
}

export function PlansScreen() {
  const [account, setAccount] = useState<AccountOut | null>(null);
  const [stats, setStats] = useState<StatsOut | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<AccountOut>("/api/account")
      .then(setAccount)
      .catch((e) => setError(e instanceof ApiError ? e.message : "ما كدرت أحمّل بيانات الحساب"));
    api.get<StatsOut>("/api/stats").then(setStats).catch(() => undefined);
  }, []);

  return (
    <div className="h-full overflow-y-auto p-4">
      {error && <p className="text-red-400 text-sm">{error}</p>}
      {!account && !error && (
        <div className="flex justify-center py-20 text-tg-hint">
          <Loader2 className="animate-spin" size={22} />
        </div>
      )}
      {account && (account.membership.tier === "free" ? <PlansView /> : <SubscriberView account={account} stats={stats} />)}
    </div>
  );
}
